import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const BASE = "https://ebikesandgear.co.uk";

const COLLECTIONS = {
  surron: {
    title: "Sur-Ron Collection",
    params: "category=surron",
    desc: "The official Sur-Ron range at UK RRP — Light Bee X, road-legal L1E, Ultra Bee, Ultra Bee HP, Storm Bee and Hyper Bee with genuine UK warranty.",
  },
  specialized: {
    title: "Specialized Collection",
    params: "search=specialized",
    desc: "Every Specialized bike, e-bike and S-Works frameset in stock — Tarmac, Aethos, Epic, Turbo Levo, Creo and more with fast EU delivery.",
  },
  trek: {
    title: "Trek Collection",
    params: "search=trek",
    desc: "The 2026 Trek range — Slash, Fuel EX, Rail+, Madone, Domane+ and Supercaliber, hand-built and delivered across the UK & EU.",
  },
  "s-works": {
    title: "S-Works Collection",
    params: "search=s-works",
    desc: "Specialized's flagship S-Works bikes and framesets — Tarmac SL9, Aethos 2, Epic World Cup, Crux and more at launch pricing.",
  },
};

export default function Collections() {
  const { name } = useParams();
  const meta = COLLECTIONS[name];
  const [products, setProducts] = useState(null);

  useEffect(() => {
    if (meta) api.get(`/products?${meta.params}&limit=100`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!meta) return <Navigate to="/shop" replace />;

  const jsonLd = [{
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: `${meta.title} — Ebikes&Gear`, description: meta.desc, url: `${BASE}/collections/${name}`,
  }, {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
      { "@type": "ListItem", position: 2, name: "Shop", item: `${BASE}/shop` },
      { "@type": "ListItem", position: 3, name: meta.title, item: `${BASE}/collections/${name}` },
    ],
  }];

  return (
    <>
      <SEO title={meta.title} description={meta.desc} path={`/collections/${name}`} jsonLd={jsonLd} />
      <section className="container-px py-12" data-testid={`collection-${name}`}>
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link><CaretRight size={12} />
          <Link to="/shop" className="hover:text-primary">Shop</Link><CaretRight size={12} />
          <span className="text-foreground/80">{meta.title}</span>
        </nav>
        <h1 className="mt-5 text-4xl sm:text-5xl font-black tracking-tighter">{meta.title}</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground leading-relaxed">{meta.desc}</p>

        {products === null ? (
          <div className="py-24 text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" /></div>
        ) : (
          <Stagger className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((p) => (
              <StaggerItem key={p.slug}><ProductCard product={p} /></StaggerItem>
            ))}
          </Stagger>
        )}

        <Reveal className="mt-14 text-center">
          <Link to="/shop" className="btn-outline">Browse the full range</Link>
        </Reveal>
      </section>
    </>
  );
}
